import { FULLBLEED_RATIO } from '@web-stories-wp/units';
/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import PropTypes from 'prop-types';
import { generatePatternStyles } from '@web-stories-wp/patterns';
import { UnitsProvider } from '@web-stories-wp/units';
import {
  memo,
  useMemo,
  useEffect,
  useRef,
  useState,
} from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import StoryPropTypes from '../../../types';
import { usePageCanvas } from '../../../app/pageCanvas';
import { TransformProvider } from '../../transform';
import DisplayElement from '../../canvas/displayElement';
import useIntersectionEffect from '../../../utils/useIntersectionEffect';

const IMAGE_TRANSITION_DELAY_MS = 300;

const Page = styled.button`
  display: block;
  position: relative;
  cursor: ${({ isInteractive }) => (isInteractive ? 'pointer' : 'default')};
  padding: 0;
  border: 0;
  background-color: transparent;
  height: ${({ height }) => height}px;
  width: ${({ width }) => width}px;
  flex: none;
  outline: 0;

  &::after {
    content: '';
    display: block;
    position: absolute;
    left: -4px;
    right: -4px;
    top: -4px;
    bottom: -4px;
    pointer-events: none;
    border-style: solid;
    border-width: 1px;
    border-radius: 8px;
    border-color: ${({ isActive, theme }) =>
      isActive ? theme.colors.border.defaultActive : 'transparent'};
  }

  ${({ isInteractive, isActive, theme }) =>
    isInteractive &&
    css`
      &:focus::after {
        border-color: ${rgba(
          theme.colors.border.selection,
          isActive ? 1 : 0.7
        )};
      }
    `}
`;

const PreviewWrapper = styled.div`
  height: 100%;
  position: relative;
  overflow: hidden;
  background-color: white;
  border-radius: 4px;
  ${({ background }) =>
    generatePatternStyles(background, 'background', null, {
      width: FULLBLEED_RATIO,
      height: 1,
    })}
`;

const Image = styled.img`
  position: relative;
  width: 100%;
  opacity: 0;
  z-index: 10;
  transition: opacity ${IMAGE_TRANSITION_DELAY_MS}ms ease-in-out;
  ${({ isLoaded }) =>
    isLoaded &&
    css`
      opacity: 1;
    `}
`;

function PagePreview({ page, label, ...props }) {
  const { backgroundColor } = page;
  const { width, height, isActive } = props;
  const pageRef = useRef(null);

  const [canRenderPreview, setCanRenderPreview] = useState(isActive);
  const [imageLoadStatus, setImageLoadStatus] = useState('loading');

  const { pageCanvas, generateDeferredPageCanvas } = usePageCanvas(
    ({ state, actions }) => ({
      pageCanvas: state.pageCanvasMap[page.id],
      generateDeferredPageCanvas: actions.generateDeferredPageCanvas,
    })
  );

  // Generate a canvas if we don't have one
  useEffect(() => {
    // Avoid frequent generation of active pages as well
    // due to rapid cache invalidation from frequent updates
    // to page.
    if (!canRenderPreview || isActive || pageCanvas) {
      return;
    }

    generateDeferredPageCanvas([page.id, page]);
  }, [
    canRenderPreview,
    page,
    pageCanvas,
    isActive,
    generateDeferredPageCanvas,
  ]);

  useIntersectionEffect(
    pageRef,
    {},
    (entry) => {
      if (entry.isIntersecting) {
        setCanRenderPreview(true);
      }
    },
    [pageRef]
  );

  // Grab image off of canvas if we got a canvas
  // from the cache
  const pageImage = useMemo(
    () => pageCanvas?.toDataURL('image/png'),
    [pageCanvas]
  );

  const pageElements = useMemo(() => page.elements, [page]);

  return (
    <UnitsProvider pageSize={{ width, height }}>
      <TransformProvider>
        <Page aria-label={label} ref={pageRef} {...props}>
          <PreviewWrapper background={backgroundColor}>
            {/* We only want to render visible page previews to improve performance */}
            {canRenderPreview ? (
              <>
                {pageImage ? (
                  <Image
                    isLoaded={
                      imageLoadStatus === 'loaded' ||
                      imageLoadStatus === 'visible'
                    }
                    draggable="false"
                    src={pageImage}
                    alt={label}
                    decoding="async"
                    onLoad={() => {
                      setImageLoadStatus('loaded');

                      setTimeout(() => {
                        setImageLoadStatus('visible');
                      }, IMAGE_TRANSITION_DELAY_MS + 100);
                    }}
                    onError={() => setImageLoadStatus('initial')}
                  />
                ) : null}

                {!pageImage || imageLoadStatus !== 'visible'
                  ? pageElements.map((element) => (
                      <DisplayElement
                        key={element.id}
                        previewMode
                        element={element}
                      />
                    ))
                  : null}
              </>
            ) : null}
          </PreviewWrapper>
        </Page>
      </TransformProvider>
    </UnitsProvider>
  );
}

PagePreview.propTypes = {
  page: StoryPropTypes.page.isRequired,
  label: PropTypes.string.isRequired,
  pageImageData: PropTypes.string,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  isInteractive: PropTypes.bool,
  isActive: PropTypes.bool,
  tabIndex: PropTypes.number,
};

export default memo(PagePreview);
